import React, { useState } from 'react';
import { AlertTriangle as AlertTriangleIcon, Mail as MailIcon } from 'lucide-react';

/**
 * EmailInvoiceModal - Modal for sending invoice emails with confirmation
 * Features:
 * - Send to booking's default email
 * - Option to send to custom email
 * - Confirmation before sending to prevent misclicks
 * - Custom subject and message (optional)
 */
function EmailInvoiceModal({ booking, onClose, onSend }) {
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [emailError, setEmailError] = useState('');

  // Get default email from booking
  const defaultEmail = booking?.user?.email || booking?.guestEmail || '';
  const bookingNumber = booking?.bookingNumber || booking?.id || 'N/A';
  const guestName = booking?.user?.name || booking?.guestName || 'Guest';

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setCustomEmail(email);
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSend = () => {
    const emailToSend = useCustomEmail ? customEmail : defaultEmail;
    
    // Validate email before sending
    if (!emailToSend) {
      setEmailError('Email address is required');
      return;
    }
    
    if (!validateEmail(emailToSend)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Prepare data to send
    const emailData = {
      bookingId: booking.id,
      email: emailToSend,
      subject: customSubject || undefined,
      htmlContent: customMessage || undefined
    };

    onSend(emailData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '550px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '2px solid #E3DFD3'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}><MailIcon size="1em" aria-hidden="true" /></span>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: '#1A3E2A'
            }}>
              Send Invoice Email
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '4px',
              lineHeight: 1
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Booking Info */}
        <div style={{
          backgroundColor: '#F5F3EB',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #E3DFD3'
        }}>
          <div style={{ fontSize: '13px', color: '#5F6B61', marginBottom: '4px' }}>
            Booking Details
          </div>
          <div style={{ fontWeight: '600', color: '#1A3E2A' }}>
            {bookingNumber} - {guestName}
          </div>
        </div>

        {/* Email Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#36433B',
            marginBottom: '8px'
          }}>
            Send To:
          </label>
          
          {/* Default Email Option */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            backgroundColor: !useCustomEmail ? '#F0F3EE' : '#F5F3EB',
            border: !useCustomEmail ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
            borderRadius: '8px',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setUseCustomEmail(false)}
          >
            <input
              type="radio"
              checked={!useCustomEmail}
              onChange={() => setUseCustomEmail(false)}
              style={{
                marginRight: '10px',
                cursor: 'pointer',
                width: '16px',
                height: '16px'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#1A3E2A', marginBottom: '2px' }}>
                Booking Email
              </div>
              <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                {defaultEmail || 'No email available'}
              </div>
            </div>
          </div>

          {/* Custom Email Option */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: '12px',
            backgroundColor: useCustomEmail ? '#F0F3EE' : '#F5F3EB',
            border: useCustomEmail ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setUseCustomEmail(true)}
          >
            <input
              type="radio"
              checked={useCustomEmail}
              onChange={() => setUseCustomEmail(true)}
              style={{
                marginRight: '10px',
                marginTop: '3px',
                cursor: 'pointer',
                width: '16px',
                height: '16px'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#1A3E2A', marginBottom: '8px' }}>
                Custom Email
              </div>
              <input
                type="email"
                value={customEmail}
                onChange={handleEmailChange}
                placeholder="Enter custom email address"
                disabled={!useCustomEmail}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: emailError ? '1px solid #A13F2F' : '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: useCustomEmail ? 'white' : '#EEEBE1',
                  color: useCustomEmail ? '#1A3E2A' : '#A5A89B',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  if (useCustomEmail) {
                    e.target.style.borderColor = '#1A3E2A';
                    e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = emailError ? '#A13F2F' : '#D2CDBF';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {emailError && useCustomEmail && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#A13F2F',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                  {emailError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Optional Custom Subject */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '14px',
            fontWeight: '600',
            color: '#36433B',
            marginBottom: '8px'
          }}>
            <span>Custom Subject (Optional)</span>
            <span style={{ fontSize: '12px', fontWeight: '400', color: '#A5A89B' }}>
              Leave blank for default
            </span>
          </label>
          <input
            type="text"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            placeholder="Your Custom Invoice - Booking #OH-1234"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1A3E2A';
              e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D2CDBF';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Optional Custom Message */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '14px',
            fontWeight: '600',
            color: '#36433B',
            marginBottom: '8px'
          }}>
            <span>Custom Message (Optional)</span>
            <span style={{ fontSize: '12px', fontWeight: '400', color: '#A5A89B' }}>
              Leave blank for default
            </span>
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add a custom message to include in the email..."
            rows={4}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1A3E2A';
              e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D2CDBF';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Warning Message */}
        <div style={{
          backgroundColor: '#F3E6CB',
          border: '1px solid #C9903C',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          display: 'flex',
          gap: '10px'
        }}>
          <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#684A1F', fontSize: '14px', marginBottom: '4px' }}>
              Confirm Before Sending
            </div>
            <div style={{ fontSize: '13px', color: '#4F3818', lineHeight: '1.5' }}>
              The invoice will be sent to <strong>{useCustomEmail ? (customEmail || 'the custom email') : defaultEmail}</strong>. 
              Please verify the email address is correct before proceeding.
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #E3DFD3'
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#E3DFD3';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#EEEBE1';
            }}
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleSend}
            disabled={!defaultEmail && !customEmail || emailError !== ''}
            style={{
              backgroundColor: (!defaultEmail && !customEmail) || emailError !== '' ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (!defaultEmail && !customEmail) || emailError !== '' ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!emailError && (defaultEmail || customEmail)) {
                e.target.style.backgroundColor = '#163626';
              }
            }}
            onMouseLeave={(e) => {
              if (!emailError && (defaultEmail || customEmail)) {
                e.target.style.backgroundColor = '#1A3E2A';
              }
            }}
          >
            <span><MailIcon size="1em" aria-hidden="true" /></span>
            Send Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailInvoiceModal;
